import { useCallback, useRef, useState } from 'react';

import { isReconnectSubscriptionError } from '@/graphql/errors';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';

import {
  HouseholdLoading_StateFragment,
  useHouseholdKgLoadingQuery,
  useHouseholdKgLoadingUpdatedSubscription,
} from '../graphql/KGLoading.generated';

export function useHouseholdKGLoading(householdID: string) {
  const { reportError } = useReportError();
  const [state, setState] = useState<HouseholdLoading_StateFragment>();
  const [unavailableHousehold, setUnavailableHousehold] = useState<string>();
  const activeRequest = useRef({
    householdID,
    eventCount: 0,
    latestRefresh: 0,
    refreshStarted: false,
  });
  if (activeRequest.current.householdID !== householdID) {
    activeRequest.current = {
      householdID,
      eventCount: 0,
      latestRefresh: 0,
      refreshStarted: false,
    };
  }
  const requestState = activeRequest.current;

  const accept = useCallback(
    (snapshot: HouseholdLoading_StateFragment) => {
      if (
        activeRequest.current !== requestState ||
        snapshot.householdID !== householdID
      )
        return;
      setState(snapshot);
      setUnavailableHousehold(undefined);
    },
    [householdID, requestState]
  );

  const { refetch } = useHouseholdKgLoadingQuery({
    variables: { householdID },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ kgLoadingState }) => {
      // Refetch responses are handled by their own promises. This callback is
      // only for the query that Apollo starts when the hook mounts.
      if (!requestState.refreshStarted && requestState.eventCount === 0) {
        accept(kgLoadingState);
      }
    },
    onError: (error) => {
      if (
        activeRequest.current !== requestState ||
        requestState.refreshStarted ||
        requestState.eventCount !== 0
      )
        return;
      setUnavailableHousehold(householdID);
      reportError('Failed to fetch household KG loading state', error, {
        householdID,
      });
    },
  });

  const refresh = useCallback(() => {
    requestState.refreshStarted = true;
    const startedAtEvent = requestState.eventCount;
    const request = ++requestState.latestRefresh;
    return refetch()
      .then(({ data }) => {
        if (
          activeRequest.current === requestState &&
          requestState.latestRefresh === request &&
          requestState.eventCount === startedAtEvent
        ) {
          accept(data.kgLoadingState);
        }
      })
      .catch((error: unknown) => {
        if (
          activeRequest.current !== requestState ||
          requestState.latestRefresh !== request ||
          requestState.eventCount !== startedAtEvent
        )
          return;
        setUnavailableHousehold(householdID);
        reportError(
          'Failed to fetch household KG loading state',
          error instanceof Error ? error : new Error(String(error)),
          { householdID }
        );
      });
  }, [accept, householdID, refetch, reportError, requestState]);

  const { restart } = useHouseholdKgLoadingUpdatedSubscription({
    variables: { householdID },
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const snapshot = data.data?.kgLoadingStateUpdated;
      if (
        activeRequest.current !== requestState ||
        !snapshot ||
        snapshot.householdID !== householdID
      )
        return;
      requestState.eventCount += 1;
      accept(snapshot);
    },
    onError: (error) => {
      if (activeRequest.current !== requestState) return;
      if (isReconnectSubscriptionError(error)) {
        restart();
        void refresh().catch(() => {
          // refresh reports accepted query failures.
        });
        return;
      }
      setUnavailableHousehold(householdID);
      reportError(
        'Failed to subscribe to household KG loading updates',
        error,
        {
          householdID,
        }
      );
    },
  });

  const resume = useCallback(() => {
    restart();
    return refresh();
  }, [restart, refresh]);

  useRefetchOnAppResume(resume, { label: `KGLoading:${householdID}` });

  // Scope both values during render; a passive reset can erase a fresh response.
  const snapshot = state?.householdID === householdID ? state : undefined;
  const unavailable = unavailableHousehold === householdID;
  return {
    snapshot,
    unavailable,
    isLoading: !!snapshot?.isLoading && !unavailable,
  };
}
